import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Copy, RefreshCw, Bot, CheckCircle2, XCircle, ExternalLink, Info } from 'lucide-react';
import { toast } from 'sonner';
import { mcp as mcpApi } from '@/lib/api';
import { BACKEND_URL, API } from '@/lib/backendUrl';

export default function MCPIntegration({ hotelId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState('');

  useEffect(() => {
    loadStatus();
  }, [hotelId]);

  const loadStatus = async () => {
    try {
      const { data } = await mcpApi.getStatus(hotelId);
      setStatus(data);
    } catch (error) {
      console.error('Failed to load MCP status:', error);
      toast.error('Failed to load MCP status');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (enabled) => {
    setSaving(true);
    try {
      const { data } = await mcpApi.enable(hotelId, enabled);
      
      if (enabled && data.api_key) {
        setRevealedKey(data.api_key);
        setShowKey(true);
        toast.success('MCP enabled! Copy your API key now - it won\'t be shown again.');
      } else {
        toast.success(data.message);
        setShowKey(false);
        setRevealedKey('');
      }
      
      await loadStatus();
    } catch (error) {
      console.error('Failed to toggle MCP:', error);
      toast.error('Failed to update MCP settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRotateKey = async () => {
    if (!confirm('⚠️ This will invalidate your current API key. All AI assistants using the old key will stop working. Continue?')) {
      return;
    }

    setSaving(true);
    try {
      const { data } = await mcpApi.rotateKey(hotelId);
      setRevealedKey(data.api_key);
      setShowKey(true);
      toast.success('New API key generated! Update your AI integrations.');
      await loadStatus();
    } catch (error) {
      console.error('Failed to rotate key:', error);
      toast.error('Failed to regenerate API key');
    } finally {
      setSaving(false);
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(revealedKey);
    toast.success('API key copied to clipboard!');
  };

  if (loading) {
    return (
      <Card data-testid="mcp-integration" className="border-purple-500/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="mcp-integration" className="border-purple-500/20 bg-gradient-to-br from-purple-500/5 to-blue-500/5">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Bot className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <CardTitle className="text-lg">AI Agent Integration (MCP)</CardTitle>
              <CardDescription className="mt-1">
                Allow AI assistants like ChatGPT, Claude, and Gemini to book your rooms directly
              </CardDescription>
            </div>
          </div>
          <Badge 
            variant={status?.enabled ? "default" : "secondary"}
            className={status?.enabled ? "bg-green-500/10 text-green-600 border-green-500/20" : ""}
            data-testid="mcp-status-badge"
          >
            {status?.enabled ? (
              <><CheckCircle2 className="w-3 h-3 mr-1" /> Active</>
            ) : (
              <><XCircle className="w-3 h-3 mr-1" /> Inactive</>
            )}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Enable Toggle */}
        <div className="flex items-center justify-between p-4 rounded-lg border border-purple-500/10 bg-white/50 dark:bg-gray-800/50">
          <div className="flex-1">
            <h4 className="font-medium text-sm mb-1">Enable MCP Bookings</h4>
            <p className="text-xs text-muted-foreground">
              Let external AI assistants discover and book your rooms
            </p>
          </div>
          <Switch
            checked={status?.enabled || false}
            onCheckedChange={handleToggle}
            disabled={saving}
            data-testid="mcp-enable-toggle"
          />
        </div>

        {/* API Key Section */}
        {status?.enabled && (
          <div className="space-y-3">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <span>Your MCP API Key</span>
              {status?.has_api_key && (
                <Badge variant="outline" className="text-xs font-normal">
                  •••• {status?.key_last4}
                </Badge>
              )}
            </h4>

            {showKey && revealedKey ? (
              <div className="space-y-3">
                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <div className="flex items-start gap-2 mb-3">
                    <Info className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-yellow-700 dark:text-yellow-600">
                      <strong>Save this key now!</strong> You won't be able to see it again. Store it securely.
                    </p>
                  </div>
                  <div className="font-mono text-sm bg-gray-900 text-green-400 p-3 rounded break-all" data-testid="mcp-api-key-display">
                    {revealedKey}
                  </div>
                </div>
                <Button
                  onClick={copyKey}
                  className="w-full"
                  variant="outline"
                  data-testid="mcp-copy-key"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy API Key
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <Alert>
                  <Info className="w-4 h-4" />
                  <AlertDescription className="text-xs">
                    Your API key is securely stored. Only the last 4 characters are visible for verification.
                  </AlertDescription>
                </Alert>
                <Button
                  onClick={handleRotateKey}
                  disabled={saving}
                  variant="outline"
                  className="w-full"
                  data-testid="mcp-rotate-key"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate API Key
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        {status?.enabled && status?.mcp_bookings_count > 0 && (
          <div className="p-4 rounded-lg border bg-gradient-to-r from-purple-500/5 to-blue-500/5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Bookings via MCP</span>
              <span className="text-2xl font-bold text-purple-600" data-testid="mcp-bookings-count">
                {status.mcp_bookings_count}
              </span>
            </div>
          </div>
        )}

        {/* Instructions */}
        {status?.enabled && (
          <div className="space-y-3 pt-3 border-t">
            <h4 className="font-medium text-sm">How to Connect AI Assistants</h4>
            <div className="space-y-2 text-xs text-muted-foreground">
              <div className="flex gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-500/10 text-purple-600 flex items-center justify-center text-xs font-medium">1</span>
                <p>Copy your MCP API key above</p>
              </div>
              <div className="flex gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-500/10 text-purple-600 flex items-center justify-center text-xs font-medium">2</span>
                <p>Add getbitsy.ai MCP server to your AI assistant's configuration</p>
              </div>
              <div className="flex gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-500/10 text-purple-600 flex items-center justify-center text-xs font-medium">3</span>
                <p>MCP Endpoint: <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-xs">POST {window.location.origin}/api/mcp</code></p>
              </div>
              <div className="flex gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-500/10 text-purple-600 flex items-center justify-center text-xs font-medium">4</span>
                <p>Pass API key in header: <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-xs">X-MCP-API-Key: your_key</code></p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="w-full mt-4"
              asChild
            >
              <a href="https://docs.easystay.ai/mcp-integration" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                View Full Documentation
              </a>
            </Button>
          </div>
        )}

        {/* Info Alert */}
        {!status?.enabled && (
          <Alert className="border-blue-500/20 bg-blue-500/5">
            <Info className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-xs text-blue-700 dark:text-blue-600">
              Enable MCP to allow AI assistants worldwide to discover and book your rooms. Your hotel will be accessible through ChatGPT, Claude, Gemini, and other AI platforms.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
